/**
 * ContactCardContent - Reusable contact display content.
 *
 * Shows contact avatar, name with lifecycle badge, subtitle (job title +
 * location), and metadata row (email, company). Optionally renders a
 * LinkedIn icon link.
 *
 * Self-hydrating: pass `entityId` + `orgId` and the card resolves its own
 * data from the centralized CRM store via `useCrmObjectSnapshot` (company
 * comes from the `ahoy_contact_to_company` association target, resolved
 * against the same store). While the store is hydrating, each field
 * renders a skeleton in its own slot — no "–" → real value flash.
 *
 * Explicit prop overrides (e.g. `name` from an action's enrichedData)
 * win over store-resolved values so callers can pre-fill a value when
 * they already have it. Passing no `entityId` disables store fetch
 * entirely — used by create-action cards where the entity doesn't exist
 * yet and every value comes from the action payload.
 */

import { Building01, Mail01 } from '@untitledui/icons';
import { useCallback, useMemo, useSyncExternalStore } from 'react';

import { Avatar } from '@/lib/base/avatar/avatar';
import { Badge } from '@/lib/base/badges/badges';
import { Skeleton } from '@/lib/base/skeleton/skeleton';
import { TextValueLabel } from '@/lib/crm/components/properties/labels/TextValueLabel';
import { useCrmObjectSnapshot } from '@/lib/crm/hooks';
import {
  getFailureVersion,
  hasFailedFetch,
  subscribeToFetchFailures,
} from '@/lib/crm/object-cards/entityFetchBatcher';
import { ActiveCampaignStatusBadge } from '@/lib/integrations/activecampaign/components/ActiveCampaignStatusBadge';
import { MailchimpStatusBadge } from '@/lib/integrations/mailchimp/components/MailchimpStatusBadge';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';
import { cx } from '@/utils/cx';

import { getDisplayName } from '../utils/getDisplayName';
import { LinkedInIcon } from '../utils/icons/CustomIcons';
import { CompactCardLayout } from './CompactCardLayout';
import { toActiveCampaignStatus, toMailchimpStatus } from './integrationStatus';
import type { MetadataItem } from './MetadataRow';
import { MetadataRow } from './MetadataRow';

export interface ContactCardContentProps {
  /**
   * Store-fetch mode: when `entityId` is provided, the card subscribes to
   * the CRM store for the contact and any fields omitted below resolve
   * automatically from the live store data. Fields that can't be
   * resolved yet render a text skeleton in their own slot so data never
   * pops in from a "–" placeholder.
   *
   * When `entityId` is undefined the card renders purely from the props
   * below (used by create-action cards where the entity doesn't exist
   * in CRM yet and the proposed values come from the action payload).
   */
  entityId?: string;
  orgId?: number;

  /** Explicit overrides — win over any value resolved from the store. */
  name?: string;
  /** Avatar image URL */
  avatarUrl?: string;
  /** Initials fallback for avatar */
  initials?: string;
  /** Lifecycle stage label (e.g., "Lead") — renders as a badge */
  lifecycleBadge?: string;
  /** LinkedIn profile URL — renders LinkedIn icon, right-aligned */
  linkedInUrl?: string;
  /** Job title */
  jobTitle?: string;
  /** Location (e.g., "Dublin, Ireland") */
  location?: string;
  /** Email address */
  email?: string;
  /** Company name or domain */
  companyName?: string;
  /** When provided, clicking the name triggers this callback (e.g. open CRM slideout) */
  onClick?: () => void;
  /** Compact mode — avatar + name + type pill only */
  compact?: boolean;
  /** Compact mode only — omit the type pill (see CompactCardLayout). */
  hideTypeBadge?: boolean;
  className?: string;
}

function buildSubtitle(jobTitle?: string, location?: string): string | null {
  if (!jobTitle && !location) {
    return null;
  }
  if (jobTitle && location) {
    return `${jobTitle} \u2022 ${location}`;
  }
  return jobTitle || location || null;
}

/** Pull a string value out of a CrmObject's properties map. */
function readStringProp(
  object: CrmObject | undefined,
  key: string
): string | undefined {
  if (!object?.properties) {
    return undefined;
  }
  const raw = getPropertyValue<string | string[]>(object.properties[key]);
  if (typeof raw === 'string') {
    return raw.trim() || undefined;
  }
  if (Array.isArray(raw) && typeof raw[0] === 'string') {
    return raw[0].trim() || undefined;
  }
  return undefined;
}

export function ContactCardContent(props: ContactCardContentProps) {
  const {
    entityId,
    orgId,
    name: nameProp,
    avatarUrl: avatarUrlProp,
    initials: initialsProp,
    lifecycleBadge: lifecycleBadgeProp,
    linkedInUrl: linkedInUrlProp,
    jobTitle: jobTitleProp,
    location: locationProp,
    email: emailProp,
    companyName: companyNameProp,
    onClick,
    compact,
    hideTypeBadge,
    className,
  } = props;

  // Subscribe to the contact's store entry (hook always runs; ensureEntity
  // short-circuits when entityId is undefined). `isSourceHydrating` is true
  // when the caller supplied an entityId but the store hasn't yet produced
  // a matching CrmObject — which is the trigger for per-field skeletons.
  const storeObject = useCrmObjectSnapshot(entityId, orgId);

  // Subscribe to fetch failure notifications so a failed fetch exits the
  // skeleton state instead of staying in "hydrating" forever.
  useSyncExternalStore(
    subscribeToFetchFailures,
    getFailureVersion,
    getFailureVersion
  );
  const isSourceHydrating =
    entityId != null && !storeObject && !hasFailedFetch(entityId);

  // The contact's "Works at" company is an association, not a property —
  // resolve the target entity separately so we can pass its display name
  // into the company metadata slot without the caller plumbing anything.
  // Skip the lookup entirely in compact mode since compact layout never
  // renders company metadata, so this would be an unnecessary fetch.
  const companyAssocId =
    compact || companyNameProp != null
      ? undefined
      : storeObject?.associations?.find(
          a => a.association_type_name === 'ahoy_contact_to_company'
        )?.target_entity_id;
  const companyObject = useCrmObjectSnapshot(companyAssocId, orgId);

  // Per-field resolution: explicit prop wins; else derive from store;
  // else undefined. `isLoading` for each field is "prop missing AND a
  // fetch that could populate it is still in flight".
  const firstName = readStringProp(storeObject, 'ahoy_first_name');
  const lastName = readStringProp(storeObject, 'ahoy_last_name');
  const derivedName =
    [firstName, lastName].filter(Boolean).join(' ') ||
    (storeObject ? getDisplayName(storeObject, '') : undefined);
  const name = nameProp ?? (derivedName || undefined);
  const isNameLoading = nameProp == null && isSourceHydrating;

  const email = emailProp ?? readStringProp(storeObject, 'ahoy_email');
  const isEmailLoading = emailProp == null && isSourceHydrating;

  const jobTitle =
    jobTitleProp ?? readStringProp(storeObject, 'ahoy_job_title');
  const location =
    locationProp ??
    readStringProp(storeObject, 'ahoy_location') ??
    readStringProp(storeObject, 'ahoy_city');
  const avatarUrl =
    avatarUrlProp ?? readStringProp(storeObject, 'ahoy_avatar_url');
  const initials = initialsProp;
  const lifecycleBadge = lifecycleBadgeProp;
  const linkedInUrl =
    linkedInUrlProp ?? readStringProp(storeObject, 'ahoy_linkedin_url');
  const mailchimpStatus = toMailchimpStatus(
    readStringProp(storeObject, 'org_mailchimp_status')
  );
  const activeCampaignStatus = toActiveCampaignStatus(
    readStringProp(storeObject, 'org_activecampaign_status')
  );

  // Company slot — resolution chain:
  //   1. Explicit companyName prop
  //   2. Resolved association target's display name (live store)
  //   3. `ahoy_company_name` property on the contact itself (rare)
  // Loading when caller didn't pass a value AND either the contact is
  // still hydrating OR we know about an association target but its
  // CrmObject hasn't hydrated yet.
  const resolvedCompanyName =
    companyNameProp ??
    (companyObject
      ? getDisplayName(companyObject, '') || undefined
      : undefined) ??
    readStringProp(storeObject, 'ahoy_company_name');
  // Only show a skeleton when there is genuinely no company name available yet.
  // If the contact's own `ahoy_company_name` property already has a value,
  // render it immediately rather than hiding it behind a skeleton while the
  // association target loads.
  const isCompanyLoading =
    companyNameProp == null &&
    !resolvedCompanyName &&
    (isSourceHydrating ||
      (!!companyAssocId && !companyObject && !hasFailedFetch(companyAssocId)));

  const handleNameClick = useCallback(
    (e: React.MouseEvent) => {
      if (onClick) {
        e.stopPropagation();
        onClick();
      }
    },
    [onClick]
  );

  const handleNameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (onClick && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }
    },
    [onClick]
  );

  const handleLinkedInClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  // useMemo so MetadataRow doesn't re-render when nothing actually changed
  // (frontend-patterns: arrays/objects passed as props must be stable).
  // Called unconditionally — if we're in compact mode we just don't use
  // the result, but the hook must run every render.
  const metadataItems = useMemo<MetadataItem[]>(
    () => [
      {
        key: 'email',
        icon: Mail01,
        fieldName: 'Email',
        label: email || '',
        isLoading: isEmailLoading,
      },
      {
        key: 'company',
        icon: Building01,
        fieldName: 'Company',
        label: resolvedCompanyName || '',
        isLoading: isCompanyLoading,
      },
    ],
    [email, isEmailLoading, resolvedCompanyName, isCompanyLoading]
  );

  const subtitle = buildSubtitle(jobTitle, location);

  // Auto-collapse to compact when there's genuinely nothing to show beneath
  // the name. The full card would otherwise paint two "–" placeholders in
  // the metadata row (email + company) with no subtitle, which reads as
  // broken. Only kicks in after hydration settles so we don't jerk from
  // compact→full when data arrives; explicit `compact` still wins.
  const hasSecondaryContent =
    !!subtitle ||
    !!email ||
    !!resolvedCompanyName ||
    isEmailLoading ||
    isCompanyLoading;
  const shouldRenderCompact =
    compact || (!isSourceHydrating && !hasSecondaryContent);

  if (shouldRenderCompact) {
    // Compact chip shows only name + avatar + type pill. We still honor
    // store-driven hydration: if no name yet, CompactCardLayout renders
    // a short skeleton label.
    return (
      <CompactCardLayout
        name={name ?? ''}
        entityTypeName='ahoy_contact'
        avatarUrl={avatarUrl}
        initials={initials}
        isLoading={isNameLoading}
        hideTypeBadge={hideTypeBadge}
        onClick={onClick}
        className={className}
      />
    );
  }

  return (
    <div className={cx('space-y-1.5', className)}>
      {/* Top row: Avatar + Name + Badge + LinkedIn (left-aligned) */}
      <div className='flex items-center gap-2'>
        <Avatar
          size='xs'
          src={avatarUrl}
          initials={
            initials ||
            (name
              ? name
                  .split(' ')
                  .map(w => w.charAt(0))
                  .join('')
                  .slice(0, 2)
                  .toUpperCase()
              : '')
          }
        />
        <div className='flex items-center gap-2 min-w-0 flex-1'>
          {isNameLoading ? (
            <Skeleton variant='text' width={140} height={14} />
          ) : (
            <span
              role={onClick ? 'button' : undefined}
              tabIndex={onClick ? 0 : undefined}
              onClick={onClick ? handleNameClick : undefined}
              onKeyDown={onClick ? handleNameKeyDown : undefined}
              className={cx(
                'text-sm font-medium text-fg-primary truncate',
                onClick &&
                  'cursor-pointer hover:underline hover:text-fg-brand-primary'
              )}
            >
              {name ?? 'New contact'}
            </span>
          )}
          {lifecycleBadge && (
            <Badge size='sm' color='brand'>
              {lifecycleBadge}
            </Badge>
          )}
          <MailchimpStatusBadge status={mailchimpStatus ?? null} />
          <ActiveCampaignStatusBadge status={activeCampaignStatus ?? null} />
        </div>
        {linkedInUrl && /^https?:\/\//i.test(linkedInUrl) && (
          <a
            href={linkedInUrl}
            target='_blank'
            rel='noopener noreferrer'
            className='shrink-0 text-fg-quaternary hover:text-fg-secondary transition-colors'
            aria-label='LinkedIn profile'
            onClick={handleLinkedInClick}
          >
            <LinkedInIcon className='w-4 h-4' />
          </a>
        )}
      </div>

      {/* Row 2: Subtitle (job title • location) — hidden when neither is set */}
      {subtitle && (
        <p className='pl-8'>
          <TextValueLabel
            value={subtitle}
            orgId={orgId}
            fontSize='xs'
            className='text-fg-secondary'
          />
        </p>
      )}

      {/* Bottom row: Email + Company (stretched, center-aligned) */}
      <MetadataRow items={metadataItems} className='pl-8' />
    </div>
  );
}
